// Services Supabase pour le systeme d'enquetes et sondages
import { supabase } from './supabaseClient';

// =====================================================
// TYPES
// =====================================================

export interface SurveyCategory {
    id: number;
    name: string;
    description: string | null;
    color: string;
    icon: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface Survey {
    id: number;
    category_id: number;
    title: string;
    description: string;
    short_description: string | null;
    start_date: string | null;
    end_date: string | null;
    target_responses: number | null;
    current_responses: number;
    is_anonymous: boolean;
    allows_multiple_responses: boolean;
    requires_authentication: boolean;
    geographic_restriction: any | null;
    age_restriction: any | null;
    status: 'draft' | 'published' | 'closed' | 'archived';
    featured: boolean;
    results_public: boolean;
    thank_you_message: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
    // Relations
    category?: SurveyCategory;
    questions?: SurveyQuestion[];
}

export interface SurveyQuestion {
    id: number;
    survey_id: number;
    question_text: string;
    question_type: 'single_choice' | 'multiple_choice' | 'text' | 'long_text' | 'number' | 'email' | 'phone' | 'date' | 'rating' | 'yes_no';
    is_required: boolean;
    order_index: number;
    help_text: string | null;
    validation_rules: any | null;
    conditional_logic: any | null;
    created_at: string;
    // Relations
    options?: SurveyQuestionOption[];
}

export interface SurveyQuestionOption {
    id: number;
    question_id: number;
    option_text: string;
    option_value: string | null;
    order_index: number;
    is_other_option: boolean;
    created_at: string;
}

export interface SurveyResponse {
    id: number;
    survey_id: number;
    respondent_id: string | null;
    session_id: string | null;
    started_at: string;
    completed_at: string | null;
    is_completed: boolean;
    time_spent_seconds: number | null;
    created_at: string;
}

export interface Poll {
    id: number;
    title: string;
    description: string | null;
    question: string;
    poll_type: 'single_choice' | 'multiple_choice' | 'yes_no';
    start_date: string;
    end_date: string | null;
    max_votes: number | null;
    current_votes: number;
    is_anonymous: boolean;
    allows_multiple_votes: boolean;
    geographic_restriction: any | null;
    status: 'active' | 'closed' | 'archived';
    results_visible: boolean;
    created_by: string | null;
    created_at: string;
    updated_at: string;
    // Relations
    options?: PollOption[];
}

export interface PollOption {
    id: number;
    poll_id: number;
    option_text: string;
    vote_count: number;
    order_index: number;
    color: string | null;
    created_at: string;
}

// =====================================================
// SERVICES CATEGORIES
// =====================================================

export const surveyCategoryService = {
    async getAll(): Promise<SurveyCategory[]> {
        const { data, error } = await supabase
            .from('survey_categories')
            .select('*')
            .eq('is_active', true)
            .order('name');

        if (error) throw error;
        return data || [];
    }
};

// =====================================================
// SERVICES ENQUETES
// =====================================================

export const surveyService = {
    // Recuperer les enquetes publiees/actives
    async getActive(options?: {
        categoryId?: number;
        featured?: boolean;
        limit?: number;
        search?: string;
    }): Promise<Survey[]> {
        let query = supabase
            .from('surveys')
            .select(`
                *,
                category:survey_categories(id, name, color, icon)
            `)
            .eq('status', 'published')
            .or(`end_date.is.null,end_date.gte.${new Date().toISOString()}`);

        if (options?.categoryId) {
            query = query.eq('category_id', options.categoryId);
        }

        if (options?.featured !== undefined) {
            query = query.eq('featured', options.featured);
        }

        if (options?.search) {
            query = query.or(`title.ilike.%${options.search}%,description.ilike.%${options.search}%`);
        }

        query = query.order('featured', { ascending: false })
            .order('created_at', { ascending: false });

        if (options?.limit) {
            query = query.limit(options.limit);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Recuperer les enquetes completees (fermees)
    async getCompleted(limit?: number): Promise<Survey[]> {
        let query = supabase
            .from('surveys')
            .select(`
                *,
                category:survey_categories(id, name, color, icon)
            `)
            .eq('status', 'closed')
            .order('end_date', { ascending: false });

        if (limit) {
            query = query.limit(limit);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Recuperer une enquete avec ses questions
    async getById(id: number): Promise<Survey | null> {
        const { data: survey, error } = await supabase
            .from('surveys')
            .select(`
                *,
                category:survey_categories(id, name, color, icon)
            `)
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            throw error;
        }

        // Recuperer les questions
        const { data: questions } = await supabase
            .from('survey_questions')
            .select(`
                *,
                options:survey_question_options(*)
            `)
            .eq('survey_id', id)
            .order('order_index');

        return {
            ...survey,
            questions: questions || []
        };
    },

    // Verifier si un membre a deja repondu
    async hasResponded(surveyId: number, memberId: string): Promise<boolean> {
        const { count } = await supabase
            .from('survey_responses')
            .select('id', { count: 'exact' })
            .eq('survey_id', surveyId)
            .eq('respondent_id', memberId)
            .eq('is_completed', true);

        return (count || 0) > 0;
    },

    // Demarrer une reponse
    async startResponse(surveyId: number, memberId?: string, sessionId?: string): Promise<SurveyResponse> {
        const { data, error } = await supabase
            .from('survey_responses')
            .insert({
                survey_id: surveyId,
                respondent_id: memberId || null,
                session_id: sessionId || null
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    // Soumettre une reponse a une question
    async submitQuestionResponse(
        responseId: number,
        questionId: number,
        answer: {
            text?: string;
            number?: number;
            date?: string;
            selectedOptions?: number[];
            otherText?: string;
        }
    ): Promise<void> {
        const { error } = await supabase
            .from('survey_question_responses')
            .upsert({
                response_id: responseId,
                question_id: questionId,
                answer_text: answer.text || null,
                answer_number: answer.number || null,
                answer_date: answer.date || null,
                selected_options: answer.selectedOptions || null,
                other_text: answer.otherText || null
            }, {
                onConflict: 'response_id,question_id'
            });

        if (error) throw error;
    },

    // Completer une reponse
    async completeResponse(responseId: number, timeSpentSeconds: number): Promise<void> {
        const { error } = await supabase
            .from('survey_responses')
            .update({
                is_completed: true,
                completed_at: new Date().toISOString(),
                time_spent_seconds: timeSpentSeconds
            })
            .eq('id', responseId);

        if (error) throw error;
    },

    // Recuperer les reponses d'un membre
    async getMemberResponses(memberId: string): Promise<Survey[]> {
        const { data, error } = await supabase
            .from('survey_responses')
            .select(`
                survey:surveys(
                    *,
                    category:survey_categories(id, name, color, icon)
                )
            `)
            .eq('respondent_id', memberId)
            .eq('is_completed', true);

        if (error) throw error;
        return (data?.map(d => d.survey).filter(Boolean) || []) as unknown as Survey[];
    }
};

// =====================================================
// SERVICES SONDAGES RAPIDES (POLLS)
// =====================================================

export const pollService = {
    // Recuperer les sondages actifs
    async getActive(limit?: number): Promise<Poll[]> {
        let query = supabase
            .from('polls')
            .select(`
                *,
                options:poll_options(*)
            `)
            .eq('status', 'active')
            .or(`end_date.is.null,end_date.gte.${new Date().toISOString()}`)
            .order('created_at', { ascending: false });

        if (limit) {
            query = query.limit(limit);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Recuperer un sondage par ID
    async getById(id: number): Promise<Poll | null> {
        const { data, error } = await supabase
            .from('polls')
            .select(`
                *,
                options:poll_options(*)
            `)
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            throw error;
        }
        return data;
    },

    // Verifier si un membre a deja vote
    async hasVoted(pollId: number, memberId: string): Promise<boolean> {
        const { count } = await supabase
            .from('poll_votes')
            .select('id', { count: 'exact' })
            .eq('poll_id', pollId)
            .eq('voter_id', memberId);

        return (count || 0) > 0;
    },

    // Voter
    async vote(pollId: number, optionId: number, memberId?: string, sessionId?: string): Promise<void> {
        const { error } = await supabase
            .from('poll_votes')
            .insert({
                poll_id: pollId,
                option_id: optionId,
                voter_id: memberId || null,
                session_id: sessionId || null
            });

        if (error) throw error;
    },

    // Recuperer les resultats d'un sondage
    async getResults(pollId: number): Promise<PollOption[]> {
        const { data, error } = await supabase
            .from('poll_options')
            .select('*')
            .eq('poll_id', pollId)
            .order('vote_count', { ascending: false });

        if (error) throw error;
        return data || [];
    }
};

// =====================================================
// STATISTIQUES
// =====================================================

export const surveyStatsService = {
    // Statistiques pour un membre
    async getMemberStats(memberId: string): Promise<{
        totalResponses: number;
        surveysCompleted: number;
        pollsVoted: number;
        opinionPoints: number;
    }> {
        const { count: surveysCompleted } = await supabase
            .from('survey_responses')
            .select('id', { count: 'exact' })
            .eq('respondent_id', memberId)
            .eq('is_completed', true);

        const { count: pollsVoted } = await supabase
            .from('poll_votes')
            .select('id', { count: 'exact' })
            .eq('voter_id', memberId);

        const totalResponses = (surveysCompleted || 0) + (pollsVoted || 0);
        // Points d'opinion: 10 points par enquete, 2 points par sondage
        const opinionPoints = ((surveysCompleted || 0) * 10) + ((pollsVoted || 0) * 2);

        return {
            totalResponses,
            surveysCompleted: surveysCompleted || 0,
            pollsVoted: pollsVoted || 0,
            opinionPoints
        };
    },

    // Statistiques globales
    async getGlobalStats(): Promise<{
        totalActiveSurveys: number;
        totalActivePolls: number;
        totalParticipants: number;
    }> {
        const { count: totalActiveSurveys } = await supabase
            .from('surveys')
            .select('id', { count: 'exact' })
            .eq('status', 'published');

        const { count: totalActivePolls } = await supabase
            .from('polls')
            .select('id', { count: 'exact' })
            .eq('status', 'active');

        const { data: surveys } = await supabase
            .from('surveys')
            .select('current_responses');

        const totalParticipants = surveys?.reduce((sum, s) => sum + (s.current_responses || 0), 0) || 0;

        return {
            totalActiveSurveys: totalActiveSurveys || 0,
            totalActivePolls: totalActivePolls || 0,
            totalParticipants
        };
    }
};
